import { useAtom } from "@dbeining/react-atom";
import { faCalendar, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Col,
    Container,
    Row,
    Stack,
    Tab,
    Tabs
} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { userAtom } from "../../atoms";
import { Documents } from "../../components/features/document";
import { Role } from "../../utils";
import { addTaskToCalender } from "../../utils";
import { emailTemplateService, taskService, userService } from "../../services";
import CustomRow from "../../components/common/Custom/CustomRow";
import { ChangeRecurringTaskDates } from "./ChangeRecurringTaskDates";
import { ChangeTaskDates } from "./ChangeTaskDates";
import mapEmailBody from "./Mapping";
import { ReAssignTask } from "./ReAssignTask";

const TaskDetailPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const actioner = `${currentUser?.identity.displayName}: ${currentUser?.identity.email}`;
    const isAdmin = currentUser?.identity.roles.some(
        (role) => role.name === Role.SystemAdmin || role.name === Role.Admin
    );
    const [task, setTask] = useState<any>();
    const [alertVariant, setAlertVariant] = useState("");
    const [message, setMessage] = useState("");

    const [notes, setNotes] = useState<string>();
    const [showReAssignTask, setShowReAssignTask] = useState(false);
    const [showChangeDates, setShowChangeDates] = useState(false);
    const [showRecurringChangeDates, setShowRecurringChangeDates] = useState(false);
    const [allUsers, setAllUsers] = useState<any[]>();
    const [emailTemplate, setEmailTemplate] = useState<any>({});
    const [taskTemplate, setTaskTemplate] = useState<any>({});
    const [adminEmail, setAdminEmail] = useState("");

    useEffect(() => {
        getFullTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        Promise.all([userService.getAdminEmail(), userService.getAllListItems()]).then((response) => {
            setAdminEmail(response[0]);
            setAllUsers(response[1]);
        });
    }, []);

    useEffect(() => {
        emailTemplateService.getAll().then(async (response) => {
            const taskCompletionTemplate = response.find((template: any) => template.name === "Task completion");
            if (taskCompletionTemplate) {
                setEmailTemplate(taskCompletionTemplate);
                setTaskTemplate(await emailTemplateService.getById(taskCompletionTemplate.id));
            } else {
                console.error("Task Completion template not found");
            }
        });
    }, []);

    const buildReceiverEmails = (personalEmail: string | undefined) => {
        const emails: string[] = [];
        if (taskTemplate.taskCreator && currentUser?.identity.email) {
            emails.push(currentUser.identity.email);
        }
        if (taskTemplate.adminEmail && adminEmail) {
            emails.push(adminEmail);
        }
        if (taskTemplate.personnel && personalEmail) {
            emails.push(personalEmail);
        }
        return emails;
    };

    const emailBody = emailTemplate.body;
    const emailSubject = emailTemplate.subject;

    const getFullTask = () => {
        if (params.id) {
            taskService.fullTask(params.id).then((t) => {
                setTask(t);
                setNotes(t.notes);
            });
        }
    };

    const completeTask = () => {
        const response = window.confirm("Do you want to complete this task? ");
        if (!response || !task) {
            return;
        }
        taskService.edit({ ...task, completedDate: new Date() }).then(
            async (res) => {
                if (res.status !== "Success") {
                    setAlertVariant("danger");
                    setMessage(res.message);
                    return;
                }
                setAlertVariant("success");
                setMessage("Task has been marked as completed");

                const pEmails: string[] = [];
                task.userId?.forEach((user: string) => {
                    const personal = allUsers?.find((item: any) => item.value === user)?.label;
                    if (personal?.includes(": ")) {
                        pEmails.push(personal.split(": ")[1]);
                    }
                });
                const targets = pEmails.length > 0 ? pEmails : [undefined];

                for (const email of targets) {
                    const receiverEmails = buildReceiverEmails(email);
                    if (receiverEmails.length === 0) {
                        continue;
                    }
                    const updatedBody = await mapEmailBody({ body: emailBody, id: task.id, actioner });
                    const updatedSubject = await mapEmailBody({ body: emailSubject, id: task.id, actioner });
                    const sendRes = await emailTemplateService.sendAutoEmail(updatedSubject, updatedBody, receiverEmails);
                    setMessage(sendRes.message ?? "Task has been marked as completed");
                }
            },
            (error) => {
                setAlertVariant("danger");
                setMessage(String(error));
            }
        );
    };

    const saveNotes = async () => {
        taskService.edit({ ...task, notes: notes }).then(
            (res) => {
                if (res.status === "Success") {
                    setAlertVariant("success");
                    setMessage("Notes have been updated successfully");
                } else {
                    setAlertVariant("danger");
                    setMessage(res.message);
                }
            },
            (error) => {
                setAlertVariant("danger");
                setMessage(String(error));
            }
        );
    };

    const viewOrAddToCalendar = async () => {
        if (!task) {
            return;
        }
        if (!task.addToCalendar) {
            addTaskToCalender(task.id).then(
                (res) => {
                    if (res.status === "Success") {
                        taskService.edit({ ...task, addToCalendar: true });
                        setAlertVariant("success");
                        setMessage("Event successfully added to the calendar");
                        setTask({ ...task, addToCalendar: true });
                    } else {
                        setAlertVariant("danger");
                        setMessage(res.message);
                    }
                },
                (error) => {
                    setAlertVariant("danger");
                    setMessage(String(error));
                }
            );
        } else {
            isAdmin ? navigate(`/diary`) : navigate(`/mycalendar`);
        }
    };

    const handleReAssignTask = (reAssigned: boolean) => {
        if (reAssigned) {
            setAlertVariant("success");
            setMessage("Task has been re assigned successfully");
            getFullTask();
        }
        setShowReAssignTask(false);
    };

    const handleOpenReAssignTask = () => {
        setShowReAssignTask(true);
    };

    const handleChangeDates = (changed: boolean) => {
        if (changed) {
            setAlertVariant("success");
            setMessage("Dates have been changed successfully");
            getFullTask();
        }
        if (task?.days) {
            setShowRecurringChangeDates(false);
        } else {
            setShowChangeDates(false);
        }
    };

    const handleOpenChangeDates = () => {
        if (task?.days) {
            setShowRecurringChangeDates(true);
        } else {
            setShowChangeDates(true);
        }
    };

    return (
        <>
            <Container fluid>
                <Helmet>
                    <title>Task Detail</title>
                </Helmet>
                {task && (
                    <>
                        <h1 className="my-3">{task.name ? `${task.name}` : "N/A"} </h1>
                        <div className="d-flex justify-content-between">
                            <Button variant="primary" onClick={viewOrAddToCalendar}>
                                <FontAwesomeIcon
                                    fixedWidth={true}
                                    icon={faCalendar}
                                    className="me-2"
                                />
                                {task.addToCalendar ? "View in Calendar" : " Add to Calendar"}
                            </Button>
                            <Stack gap={3} direction="horizontal">
                                {isAdmin && (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={handleOpenReAssignTask}
                                        >
                                            Reassign
                                        </Button>
                                        <Button variant="secondary" onClick={handleOpenChangeDates}>
                                            Change Dates
                                        </Button>
                                    </>
                                )}
                                <Button
                                    variant="primary"
                                    className="ms-auto"
                                    onClick={completeTask}
                                    disabled={!!task.completedDate}
                                >
                                    <FontAwesomeIcon
                                        fixedWidth={true}
                                        icon={faCheck}
                                        className="me-2"
                                    />
                                    Mark Completed
                                </Button>
                            </Stack>
                        </div>
                        <br />
                        <CustomRow label="Type" value={task.taskTypeName} />
                        <CustomRow label="Personnel" value={task.userName} />
                        <CustomRow
                            label="Start time"
                            value={dayjs(task.creationDate).format("ddd DD MMM YYYY - HH:mm")}
                        />
                        {task.days && (
                            <p className="text-center">
                                Recurring on {task.days} every {task.recursiveInterval} weeks
                            </p>
                        )}
                        <CustomRow
                            label="End/Due time"
                            value={dayjs(task.dueDate).format("ddd DD MMM YYYY - HH:mm")}
                        />
                        {task.location && (
                            <CustomRow label="Location" value={<a href={`https://maps.google.com/?q=${encodeURIComponent(task.location)}`} target="_blank" rel="noreferrer">{task.location}</a>} />
                        )}
                        <Row className="mt-5">
                            <Col md={3}>Notes</Col>
                            <Col md={9}>
                                <Form>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="exampleForm.ControlTextarea1"
                                    >
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={notes}
                                            onChange={(event) => setNotes(event.target.value)}
                                        />
                                    </Form.Group>
                                </Form>
                            </Col>
                        </Row>
                        <Stack direction="horizontal" gap={3}>
                            <div className="ms-auto">
                                <Button type="submit" variant="primary" onClick={saveNotes}>
                                    Save notes
                                </Button>
                            </div>
                        </Stack>
                        <Alert variant={alertVariant as any} className="mt-3">
                            {message}
                        </Alert>
                    </>
                )}
            </Container>
            <ReAssignTask
                show={showReAssignTask}
                onClose={handleReAssignTask}
                taskId={params.id}
            />
            <ChangeTaskDates
                show={showChangeDates}
                onClose={handleChangeDates}
                taskId={params.id}
            />
            <ChangeRecurringTaskDates
                show={showRecurringChangeDates}
                onClose={handleChangeDates}
                taskId={params.id}
            />
            <Row>
                <Col>
                    <Tabs defaultActiveKey="documents" className="mb-3" mountOnEnter>
                        <Tab eventKey="documents" title="Documents">
                            <Documents />
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </>
    );
};

export { TaskDetailPage };
